// src/components/shell/FlashToast.js
// Notification flottante : confirmation d'ordre, erreur d'envoi.
//
// Pourquoi elle ne vit pas dans le flux de la page : la confirmation
// « Commande envoyée avec succès » était un bandeau inséré au-dessus du
// formulaire. Il apparaissait, poussait tout le contenu vers le bas, puis
// disparaissait deux secondes plus tard et le contenu remontait. En pleine
// prise de position, les boutons Buy/Sell se déplaçaient sous le curseur.
//
// Ici, `fixed` + portail vers <body> : la page ne bouge pas d'un pixel.
import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import { CheckCircle2, AlertTriangle, X } from "lucide-react";

/**
 * @param {"success"|"error"} tone
 * @param {string} text      message ; vide = rien n'est rendu
 * @param {() => void} onClose
 */
export default function FlashToast({ tone = "success", text, onClose }) {
  // Échap ferme, comme n'importe quelle notification.
  useEffect(() => {
    if (!text) return undefined;
    const onKey = (e) => e.key === "Escape" && onClose?.();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [text, onClose]);

  if (!text) return null;

  const isError = tone === "error";
  const Icon = isError ? AlertTriangle : CheckCircle2;

  const node = (
    <div
      // `pointer-events-none` sur l'enveloppe : elle couvre le bas de l'écran,
      // sans ce garde-fou elle avalerait les clics sur les boutons en dessous.
      className="pointer-events-none fixed inset-x-0 bottom-0 z-[70] flex justify-center px-4 pb-[max(1rem,env(safe-area-inset-bottom))] sm:justify-end sm:px-6"
      role="status"
      aria-live="polite"
    >
      <div
        className={[
          "pointer-events-auto flex w-full max-w-sm items-start gap-3 rounded-2xl border px-4 py-3 shadow-lg backdrop-blur",
          "animate-[fmToastIn_180ms_ease-out]",
          isError
            ? "border-rose-500/25 bg-rose-50/95 text-rose-800 dark:border-rose-400/25 dark:bg-[#2a1220]/95 dark:text-rose-200"
            : "border-emerald-500/25 bg-emerald-50/95 text-emerald-800 dark:border-emerald-400/25 dark:bg-[#0d2320]/95 dark:text-emerald-200",
        ].join(" ")}
      >
        <Icon size={18} className="mt-0.5 shrink-0" />
        <div className="min-w-0 flex-1 text-sm font-medium leading-relaxed">
          {text}
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Fermer"
          className="-mr-1 -mt-1 shrink-0 rounded-lg p-1.5 opacity-70 transition hover:opacity-100"
        >
          <X size={15} />
        </button>
      </div>
    </div>
  );

  if (typeof document === "undefined") return node;
  return createPortal(node, document.body);
}
